import type { CaptureResult, CapturedNetworkRequest } from "posthog-js"

import { DARK_READER_FLAG, PAGE_TRANSLATED_FLAG } from "@/lib/translate-dom-guard"

/** Query parameters whose value is a live credential. PostHog copies the page
 * URL into $current_url, $referrer (the page after a button press),
 * $session_entry_url and the person's $initial_current_url and
 * $initial_referrer, keys $$heatmap data by it, and autocapture copies link
 * hrefs into $elements_chain, so without this everyone with PostHog access
 * would hold live credentials.
 * - `token_hash`: the single-use sign-in token our emails put on
 *   /sign-in/set-password and /sign-in/confirm; whoever holds it can press
 *   that page's button.
 * - `payment_intent_client_secret`, `setup_intent_client_secret`: Stripe's
 *   client secrets on /pay/return (Stripe appends them to the return_url, and
 *   the in-page confirm appends the same names); whoever holds one can read and
 *   confirm that intent with our publishable key.
 * - `payment_intent`, `setup_intent`: the ids of those intents, beside their
 *   secrets on /pay/return. Not a credential, but each names one buyer's
 *   payment, and no analytics reader uses it (the return page's own
 *   payment_return_viewed event says how the payment ended). redirect_status,
 *   the third parameter Stripe appends, names no payment and stays.
 * posthog-js also masks these itself (`custom_personal_data_properties` in
 * instrumentation-client.ts), which is what reaches the copies before_send never
 * sees: its stored first visit, sent as the person's $initial_* properties on
 * every /flags request and kept in its first-party cookie. */
export const SECRET_URL_PARAMS = [
  "token_hash",
  "payment_intent_client_secret",
  "setup_intent_client_secret",
  "payment_intent",
  "setup_intent",
]

/** What PostHog's own query masking writes in place of a masked value. */
export const MASKED_VALUE = "<masked>"

// The name must start a parameter: after ? & # ; whitespace or the start, or
// the percent-encoded ? & # when the URL sits inside another URL's parameter
// (a callbackUrl). The value runs to the next separator, plain or encoded. So
// not_token_hash= and payment_intent_client_secret_hint= are never matched.
const SECRET_PARAM_RE = new RegExp(
  `((?:^|[?&#;\\s]|%3F|%26|%23)(?:${SECRET_URL_PARAMS.join("|")})(?:=|%3D))((?:(?!%26|%23)[^&#;\\s"'])+)`,
  "gi",
)
// Cheap pre-check so a string naming none of the parameters skips the replace.
const SECRET_PARAM_NAME_RE = new RegExp(SECRET_URL_PARAMS.join("|"), "i")

/** The string with every secret parameter's value replaced. */
export function redactSecretUrlParams(value: string): string {
  if (!SECRET_PARAM_NAME_RE.test(value)) return value
  return value.replace(SECRET_PARAM_RE, `$1${MASKED_VALUE}`)
}

/** The session_recording `maskCapturedNetworkRequestFn`. posthog-js runs every
 * recorded network request through it, and also the replay's own page URL (the
 * rrweb meta href and its $pageview and $url_changed markers, passed as a bare
 * `{ name }`). A private messaging route is dropped outright. Otherwise the
 * query string AND the fragment go: a Stripe client secret rides in the query
 * of /pay/return and of the status poll behind it, and Stripe's own iframes
 * carry the page URL, percent-encoded, in their fragment.
 * Request and response bodies are never kept. */
export function maskReplayNetworkRequest(request: CapturedNetworkRequest): CapturedNetworkRequest | null {
  if (isPrivateMessagingObservabilityPath(request.name)) return null
  request.name = request.name.split(/[?#]/)[0]
  request.requestBody = undefined
  request.responseBody = undefined
  return request
}

// Replay content is not scanned here: rrweb's events are large and partly
// compressed, the URLs inside them already go through maskReplayNetworkRequest
// above (which drops every query string and fragment), and the hidden token
// fields carry the ph-no-capture block class.
const SKIPPED_KEYS = new Set(["$snapshot_data"])
const MAX_DEPTH = 8

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === "string") return redactSecretUrlParams(value)
  if (depth >= MAX_DEPTH || value === null || typeof value !== "object") return value
  if (Array.isArray(value)) {
    let changed = false
    const next = value.map((item) => {
      const scrubbed = scrubValue(item, depth + 1)
      if (scrubbed !== item) changed = true
      return scrubbed
    })
    return changed ? next : value
  }
  if (Object.getPrototypeOf(value) !== Object.prototype) return value
  let changed = false
  const next: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value)) {
    // Keys too: $$heatmap data is keyed by the page URL.
    const scrubbedKey = redactSecretUrlParams(key)
    const scrubbed = SKIPPED_KEYS.has(key) ? item : scrubValue(item, depth + 1)
    if (scrubbedKey !== key || scrubbed !== item) changed = true
    next[scrubbedKey] = scrubbed
  }
  return changed ? next : value
}

/** The PostHog `before_send` scrub for every event: the event with each secret
 * URL parameter's value masked in its properties, $set and $set_once, keys
 * included, and unchanged (the same object) when there is nothing to mask. */
export function scrubPosthogEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event
  return scrubValue(event, 0) as CaptureResult
}

/** Where posthog-js keeps the first page a browser ever loaded (`u`) and its
 * referrer (`r`). It sends them outside before_send, as the person's $initial_*
 * properties on every /flags request and in its first-party cookie. */
const STORED_FIRST_VISIT_KEY = "$initial_person_info"

type StoredPropertiesApi = {
  get_property(name: string): unknown
  register(properties: Record<string, unknown>): void
}

/** Masks the secret parameters in a first visit the browser stored before
 * posthog-js masked them at the source. It is kept for good (posthog-js never
 * rewrites it), so without this a browser whose first page was /pay/return
 * would send that URL on every later /flags request. Uses posthog-js's public
 * get_property/register, which read and write that stored key. Run right after
 * `posthog.init`: the first /flags request is built on a later tick. True when
 * it rewrote the stored value. */
export function repairStoredFirstVisit(ph: StoredPropertiesApi): boolean {
  const stored = ph.get_property(STORED_FIRST_VISIT_KEY)
  if (!stored || typeof stored !== "object" || Array.isArray(stored)) return false
  const scrubbed = scrubValue(stored, 0)
  if (scrubbed === stored) return false
  ph.register({ [STORED_FIRST_VISIT_KEY]: scrubbed })
  return true
}

type ReferrerDocument = Pick<Document, "createElement" | "head">

/** Marks the referrer policy keepCredentialUrlOutOfReferrers set, so only that
 * one is ever lifted. */
const CREDENTIAL_REFERRER_MARK = "data-credential-url"

/** The policy while the URL holds a credential: only the origin, and nothing
 * at all from https to http. */
const CREDENTIAL_REFERRER_POLICY = "strict-origin"

/** A page carrying a live credential in its URL (/pay/return's Stripe client
 * secret, a sign-in token_hash) sends only its origin as the referrer from then
 * on. The browser otherwise puts the full URL in the Referer header of every
 * same-origin request the page makes, and the SDK's script loads (its remote
 * config, web vitals, the recorder) and its unload beacon go to /ingest, which
 * forwards them to PostHog; posthog-js sets `strict-origin` on its own fetches
 * but cannot on those. It also keeps the URL out of the next page's
 * document.referrer. Run before `posthog.init`. True when it set the policy.
 * The policy lasts only while the URL holds the credential: the site is one
 * document across client-side steps, so releaseCredentialReferrerPolicy lifts
 * it on the first step to a URL without one, or every later full page load in
 * the tab would name only the homepage as where he came from, and sets it
 * again on any step back to a URL with one. */
export function keepCredentialUrlOutOfReferrers(doc: ReferrerDocument, href: string): boolean {
  if (!doc.head || redactSecretUrlParams(href) === href) return false
  const meta = doc.createElement("meta")
  meta.setAttribute("name", "referrer")
  meta.setAttribute("content", CREDENTIAL_REFERRER_POLICY)
  meta.setAttribute(CREDENTIAL_REFERRER_MARK, "")
  doc.head.appendChild(meta)
  return true
}

/** The site's own policy, the Referrer-Policy header in next.config.ts. */
const SITE_REFERRER_POLICY = "strict-origin-when-cross-origin"

/** Fits the referrer policy to the URL a client-side step is moving to, on
 * every kind of step (Next's onRouterTransitionStart, a push, a replace or a
 * Back/Forward traverse). A URL holding no credential lifts the policy
 * keepCredentialUrlOutOfReferrers set, back to the site's own. A URL holding
 * one sets origin-only again: pressing Back onto /pay/return puts the client
 * secret back in the URL, and a lifted policy would hand it on in the Referer
 * of every request after. Removing the meta would not lift it: a document
 * keeps a meta's policy after the meta is gone, and only a changed content
 * attribute sets it again. True when it changed the policy. */
export function releaseCredentialReferrerPolicy(doc: ReferrerDocument, href: string): boolean {
  if (!doc.head) return false
  const carriesCredential = redactSecretUrlParams(href) !== href
  const meta = doc.head.querySelector(`meta[name="referrer"][${CREDENTIAL_REFERRER_MARK}]`)
  if (!meta) return carriesCredential ? keepCredentialUrlOutOfReferrers(doc, href) : false
  const policy = carriesCredential ? CREDENTIAL_REFERRER_POLICY : SITE_REFERRER_POLICY
  if (meta.getAttribute("content") === policy) return false
  meta.setAttribute("content", policy)
  return true
}

// ---------------------------------------------------------------------------
// Error tracking. PostHog is the one error tracker (context/error-pipeline.md):
// the browser captures through posthog-js and the server through posthog-node
// (lib/observability/posthog-errors.ts). Everything below is shared by both, so
// it imports nothing server-only.
// ---------------------------------------------------------------------------

/** The owner's private messaging surfaces. No analytics event and no exception
 * ever leaves the app from these, in the browser or on the server. */
const PRIVATE_MESSAGING_PATHS = [
  "/admin/decisions",
  "/api/admin/ops/decide",
  "/api/admin/actions",
]

export function isPrivateMessagingObservabilityPath(value: unknown): boolean {
  if (typeof value !== "string" || !value) return false
  const candidates = [value.split("?")[0]]
  try {
    candidates.push(new URL(value, "https://local.invalid").pathname)
  } catch {
    // The raw route label is still checked below.
  }
  return PRIVATE_MESSAGING_PATHS.some((prefix) =>
    candidates.some(
      (candidate) =>
        candidate === prefix ||
        candidate.startsWith(`${prefix}/`) ||
        candidate.includes(` ${prefix}`),
    ),
  )
}

/** The money paths: the pay and apply pages and the routes behind them,
 * including `/api/coaching/apply` and its `/resume`, which the /apply form
 * itself posts to. An exception raised on one carries `ca_money_path: true`,
 * and the PostHog bypass rule on that property lets it past every error
 * tracking rate limit, so a burst of unrelated noise can never crowd a checkout
 * or application failure out of an hour's bucket. */
const MONEY_PATH_RE =
  /^\/(?:pay|apply)(?:\/|$)|^\/api\/(?:stripe|paypal|apply|coaching\/apply|community\/checkout)(?:\/|$)/

export function isMoneyPath(value: unknown): boolean {
  if (typeof value !== "string" || !value) return false
  let path = value.split("?")[0]
  try {
    path = new URL(value, "https://local.invalid").pathname
  } catch {
    // Keep the query-stripped value.
  }
  return MONEY_PATH_RE.test(path)
}

/** Secret-shaped `name=value` pairs inside an exception message or a URL. An
 * exception message can quote a request URL, so the value is replaced before
 * it reaches PostHog or the GitHub issue built from it. */
const SECRET_PATTERN =
  /(^|[?&#\s;])((?:access|refresh|id)_token|api_key|token|email|code|key|secret|password|auth|session)=[^&#\s;]*/gi

export function redactSecretValues(value: string): string {
  // The URL credentials too: the server's posthog-node client runs only this
  // exception redaction, never scrubPosthogEvent.
  return redactSecretUrlParams(value.replace(SECRET_PATTERN, "$1$2=[REDACTED]"))
}

// This is deliberately error-diagnostic-only. `redactSecretValues` is also
// used by lead-story text and keeps its existing behavior; general analytics
// events continue to use their separate URL scrubber.
const ERROR_DIAGNOSTIC_CREDENTIAL_KEYS = new Set([
  "apikey",
  "apisecret",
  "accesstoken",
  "refreshtoken",
  "idtoken",
  "sessiontoken",
  "csrftoken",
  "token",
  "authorization",
  "proxyauthorization",
  "bearer",
  "clientsecret",
  "webhooksecret",
  "secret",
  "secretkey",
  "secretaccesskey",
  "privatekey",
  "signingkey",
  "accesskey",
  "password",
  "passwd",
  "sessioncookie",
  "cookie",
  "cookies",
  "setcookie",
  "auth",
  "authtoken",
  "credential",
  "credentials",
])
const ERROR_DIAGNOSTIC_CREDENTIAL_NAME_SOURCE =
  "(?:api[_.-]?key|api[_.-]?secret|(?:access|refresh|id|session|csrf)[_.-]?token|session[_.-]?cookies?|token|authorization|proxy[_.-]?authorization|bearer|client[_.-]?secret|webhook[_.-]?secret|secret(?:[_.-]?(?:access[_.-]?key|key))?|private[_.-]?key|signing[_.-]?key|access[_.-]?key|password|passwd|cookies?|set[_.-]?cookie|auth(?:[_.-]?token)?|credentials?)"
const ERROR_DIAGNOSTIC_CREDENTIAL_KEY_SOURCE = `(?:[a-z0-9]+[_.-]+)*${ERROR_DIAGNOSTIC_CREDENTIAL_NAME_SOURCE}`
const ERROR_DIAGNOSTIC_CREDENTIAL_KEY_RE = new RegExp(`^${ERROR_DIAGNOSTIC_CREDENTIAL_KEY_SOURCE}$`, "i")
const ERROR_DIAGNOSTIC_JSON_CREDENTIAL_RE = new RegExp(
  `((?:\\\\?["'])${ERROR_DIAGNOSTIC_CREDENTIAL_KEY_SOURCE}(?:\\\\?["']\\s*:\\s*\\\\?["']))(.*?)(\\\\?["'])(?=\\s*[,}\\]]|$)`,
  "gi",
)
const ERROR_DIAGNOSTIC_ASSIGNMENT_RE = new RegExp(
  `(^|[?&#;\\s,{\\[]|\\\\)(${ERROR_DIAGNOSTIC_CREDENTIAL_KEY_SOURCE})(\\s*(?:=|:)\\s*)(?:Bearer\\s+)?([^\\s?&#;,"'\\[\\]}]+)`,
  "gi",
)
const ERROR_DIAGNOSTIC_AUTH_HEADER_RE = /\b((?:proxy-)?authorization\s*:\s*)[^\r\n,;]+/gi
const ERROR_DIAGNOSTIC_COOKIE_HEADER_RE = /\b(set-cookie|cookie)\s*:\s*[^\r\n]+/gi
const ERROR_DIAGNOSTIC_BEARER_RE = /\bBearer\s+[A-Za-z0-9._~+\/-]+=*/gi
const ERROR_DIAGNOSTIC_URL_USERINFO_RE = /\b([a-z][a-z\d+.-]*:\/\/)[^/@\s]+@/gi
const ERROR_DIAGNOSTIC_REDACTED = "[REDACTED]"
const ERROR_DIAGNOSTIC_OMITTED = "[omitted unsafe diagnostic value]"

function isErrorDiagnosticCredentialKey(key: string): boolean {
  return (
    ERROR_DIAGNOSTIC_CREDENTIAL_KEYS.has(key.replace(/[^a-z\d]/gi, "").toLowerCase()) ||
    ERROR_DIAGNOSTIC_CREDENTIAL_KEY_RE.test(key)
  )
}

function defineErrorDiagnosticProperty(target: object, key: string, value: unknown): void {
  Object.defineProperty(target, key, {
    value,
    enumerable: true,
    configurable: true,
    writable: true,
  })
}

function disableErrorDiagnosticToJSON(target: object): void {
  Object.defineProperty(target, "toJSON", {
    value: undefined,
    enumerable: false,
    configurable: true,
    writable: true,
  })
}

function hasErrorDiagnosticToJSON(value: object): boolean {
  let current: object | null = value
  while (current) {
    if (Object.getOwnPropertyDescriptor(current, "toJSON")) return true
    current = Object.getPrototypeOf(current)
  }
  return false
}

type ErrorDiagnosticFrame = {
  source: object
  target: object
  keys: string[]
  index: number
  isArray: boolean
  changed: boolean
  parent?: { target: object; key: string }
}

function isErrorDiagnosticArrayIndex(key: string): boolean {
  const index = Number(key)
  return Number.isInteger(index) && index >= 0 && index < 0xffff_ffff && String(index) === key
}

/** Redact known credential forms in error text. This intentionally does not
 * guess at secrets without a recognized credential label or URL userinfo. */
export function sanitizeErrorDiagnosticString(value: string): string {
  return redactSecretValues(value)
    .replace(ERROR_DIAGNOSTIC_JSON_CREDENTIAL_RE, `$1${ERROR_DIAGNOSTIC_REDACTED}$3`)
    .replace(ERROR_DIAGNOSTIC_AUTH_HEADER_RE, `$1${ERROR_DIAGNOSTIC_REDACTED}`)
    .replace(ERROR_DIAGNOSTIC_COOKIE_HEADER_RE, `$1: ${ERROR_DIAGNOSTIC_REDACTED}`)
    .replace(ERROR_DIAGNOSTIC_ASSIGNMENT_RE, `$1$2$3${ERROR_DIAGNOSTIC_REDACTED}`)
    .replace(ERROR_DIAGNOSTIC_BEARER_RE, `Bearer ${ERROR_DIAGNOSTIC_REDACTED}`)
    .replace(ERROR_DIAGNOSTIC_URL_USERINFO_RE, `$1${ERROR_DIAGNOSTIC_REDACTED}@`)
}

/** Sanitize structured error diagnostics before serialization or telemetry.
 * An explicit work stack handles deep values without imposing data caps. Only
 * actual own descriptors are visited, so sparse array holes are not traversed.
 * Accessors and toJSON hooks are never invoked; cycles and unsupported objects
 * become fixed markers instead of raw string fallbacks. */
export function sanitizeErrorDiagnosticValue(value: unknown): unknown {
  const prepare = (current: unknown): { value: unknown; frame?: ErrorDiagnosticFrame } => {
    if (typeof current === "string") return { value: sanitizeErrorDiagnosticString(current) }
    if (current === null || current === undefined || typeof current === "boolean" || typeof current === "number") {
      return { value: current }
    }
    if (typeof current === "bigint") return { value: current.toString() }
    if (typeof current === "symbol" || typeof current === "function") return { value: ERROR_DIAGNOSTIC_OMITTED }
    if (typeof current !== "object") return { value: ERROR_DIAGNOSTIC_OMITTED }

    try {
      if (current instanceof Date) {
        try {
          return { value: sanitizeErrorDiagnosticString(Date.prototype.toISOString.call(current)) }
        } catch {
          return { value: "[invalid date]" }
        }
      }
      if (current instanceof URL) {
        try {
          return { value: sanitizeErrorDiagnosticString(URL.prototype.toString.call(current)) }
        } catch {
          return { value: ERROR_DIAGNOSTIC_OMITTED }
        }
      }

      if (Array.isArray(current)) {
        const prototype = Object.getPrototypeOf(current)
        const length = Object.getOwnPropertyDescriptor(current, "length")?.value
        if (typeof length !== "number" || !Number.isInteger(length) || length < 0 || length > 0xffff_ffff) {
          return { value: ERROR_DIAGNOSTIC_OMITTED }
        }
        const hasToJSON = hasErrorDiagnosticToJSON(current)
        const keys = Reflect.ownKeys(current).filter(
          (key): key is string => typeof key === "string" && isErrorDiagnosticArrayIndex(key),
        )
        const target: unknown[] = new Array<unknown>(length)
        if (hasToJSON) disableErrorDiagnosticToJSON(target)
        return {
          value: undefined,
          frame: {
            source: current,
            target,
            keys,
            index: 0,
            isArray: true,
            changed: hasToJSON || prototype !== Array.prototype,
          },
        }
      }

      const prototype = Object.getPrototypeOf(current)
      if (prototype !== Object.prototype && prototype !== null) return { value: ERROR_DIAGNOSTIC_OMITTED }
      const hasToJSON = hasErrorDiagnosticToJSON(current)
      const keys = Reflect.ownKeys(current).filter((key): key is string => typeof key === "string")
      const target: Record<string, unknown> = prototype === null ? Object.create(null) : {}
      if (hasToJSON) disableErrorDiagnosticToJSON(target)
      return {
        value: undefined,
        frame: {
          source: current,
          target,
          keys,
          index: 0,
          isArray: false,
          changed: hasToJSON,
        },
      }
    } catch {
      return { value: ERROR_DIAGNOSTIC_OMITTED }
    }
  }

  const root = prepare(value)
  if (!root.frame) return root.value

  const active = new WeakSet<object>([root.frame.source])
  const stack: ErrorDiagnosticFrame[] = [root.frame]
  let result: unknown = root.frame.source
  while (stack.length > 0) {
    const frame = stack[stack.length - 1]
    if (frame.index >= frame.keys.length) {
      active.delete(frame.source)
      stack.pop()
      if (frame.changed) {
        if (frame.parent) {
          defineErrorDiagnosticProperty(frame.parent.target, frame.parent.key, frame.target)
          const parentFrame = stack[stack.length - 1]
          if (parentFrame) parentFrame.changed = true
        } else {
          result = frame.target
        }
      }
      continue
    }

    const key = frame.keys[frame.index++]
    if (!frame.isArray && key === "toJSON") {
      frame.changed = true
      continue
    }

    let descriptor: PropertyDescriptor | undefined
    try {
      descriptor = Object.getOwnPropertyDescriptor(frame.source, key)
    } catch {
      defineErrorDiagnosticProperty(frame.target, key, ERROR_DIAGNOSTIC_OMITTED)
      frame.changed = true
      continue
    }
    if (!descriptor) continue
    if (!frame.isArray && !descriptor.enumerable) continue
    if (!frame.isArray && isErrorDiagnosticCredentialKey(key)) {
      defineErrorDiagnosticProperty(frame.target, key, ERROR_DIAGNOSTIC_REDACTED)
      frame.changed = true
      continue
    }
    if (!("value" in descriptor)) {
      defineErrorDiagnosticProperty(frame.target, key, ERROR_DIAGNOSTIC_OMITTED)
      frame.changed = true
      continue
    }

    const item = descriptor.value
    if (item !== null && typeof item === "object" && active.has(item)) {
      defineErrorDiagnosticProperty(frame.target, key, "[circular diagnostic reference]")
      frame.changed = true
      continue
    }

    const prepared = prepare(item)
    if (prepared.frame) {
      defineErrorDiagnosticProperty(frame.target, key, item)
      prepared.frame.parent = { target: frame.target, key }
      active.add(prepared.frame.source)
      stack.push(prepared.frame)
    } else {
      defineErrorDiagnosticProperty(frame.target, key, prepared.value)
      if (prepared.value !== item) frame.changed = true
    }
  }

  return result
}

// Browser noise that is not a product defect: a browser extension's injected
// script throwing, Chrome's ResizeObserver notification-loop warning, a fetch
// aborted by navigation, ad-blocker and offline "failed to fetch" noise that is
// indistinguishable from a real outage by message alone, the Android WebView
// JS-bridge teardown ("Java object is gone") and extensions or Google Translate
// mutating the DOM under React (the insertBefore NotFoundError), and a crypto
// wallet's in-app browser or extension poking the provider it injects (the site
// has no wallet code, so `window.ethereum` or a sibling provider global in a
// message is never ours).
const EXTENSION_SCHEME_RE = /\b(?:chrome|moz|safari-web|safari|edge)-extension:\/\//i
const KNOWN_NOISE_MESSAGE_RE = [
  /ResizeObserver loop/i,
  /^AbortError/i,
  /the (user aborted|operation was aborted)/i,
  /^Failed to fetch$/i,
  /^NetworkError when attempting to fetch resource\.?$/i,
  /^Load failed$/i,
  /Java object is gone/i,
  /Failed to execute 'insertBefore' on 'Node'/i,
  // Only the injected wallet provider globals, written as a property access.
  /\bwindow\.(?:ethereum|solana|tronLink|tronWeb)\b/i,
]

// A bare "Script error." is the browser masking an error thrown by a script it
// will not describe: an injected wallet or in-app-browser script, or a
// cross-origin script served without CORS headers. The message carries no
// stack, so it cannot be acted on, and the issue-filing paths skip it. It is
// NOT dropped on a pay or apply page: those load Stripe, PayPal and Cal scripts
// cross-origin, and a bare "Script error." is the only trace when one of them
// throws, so there the event is kept and marked `ca_money_path`. Anchored so a
// longer message that contains these words is still a real error.
const MASKED_SCRIPT_ERROR_RE = /^Script error\.?$/

// Opera's built-in AI features and agent mode rewrite a page's DOM before React
// hydrates it, so the server HTML no longer matches (minified React errors 418,
// 423 and 425). Two such events from one Opera profile, in the same
// millisecond on two different articles, hydrated cleanly on the dev build in
// Chromium with the Opera user agent at desktop and phone widths. Only Opera is
// treated as this noise, so a hydration mismatch in any other browser still
// reaches the issue tracker, and never on a money path.
const HYDRATION_MISMATCH_RE = /Minified React error #(?:418|423|425)\b/

function isHydrationMismatch(properties: Record<string, unknown> | undefined): boolean {
  return exceptionEntries(properties).some(
    (entry) => typeof entry.value === "string" && HYDRATION_MISMATCH_RE.test(entry.value),
  )
}

export function isOperaHydrationNoise(properties: Record<string, unknown> | undefined): boolean {
  return properties?.$browser === "Opera" && isHydrationMismatch(properties)
}

// A browser that translates the page before React hydrates it (Chrome's
// built-in translation, Google Translate) changes the text React expects, so
// React reports a hydration mismatch (minified error 418) and re-renders the
// page; nothing is broken and nothing in our code differs. A visitor on Chrome
// in Italian hit exactly this on /bootcamp (PostHog issue 01a0ee6b, the
// session's clicks landing on Google Translate's <font> wrappers), and the
// same translation run before hydration on the dev build produces the same
// error. Dropped only when the browser had translated the page, so a mismatch
// for every other visitor still reaches the issue tracker, and never on a
// money path.
function isTranslatedHydrationNoise(
  properties: Record<string, unknown> | undefined,
  doc: TranslationProbeDocument | undefined,
): boolean {
  return doc != null && isHydrationMismatch(properties) && isPageTranslated(doc)
}

// React 19 words error 418 as "server rendered HTML" when the element tree
// differs and "server rendered text" when only a text node does.
const ELEMENT_HYDRATION_MISMATCH_RE = /Minified React error #418\b[^]*[?&]args\[\]=HTML(?:&|\s|$)/

// The Dark Reader extension inserts <style class="darkreader"> siblings next to
// our inline <style> blocks inside <body> before React hydrates (on every page
// that has one), so React finds extra elements, reports error 418 with the
// argument HTML and re-renders; nothing in our code differs (reproduced with
// the official extension on production, 2 of 2 with it and 0 of 2 without).
// Dropped only for that element mismatch while the extension is present, so a
// text mismatch (a real one: a clock, a locale or a time zone read during
// render) and every other error on a Dark Reader user's page still reaches the
// issue tracker, and never on a money path. An element mismatch that our own
// code causes on a Dark Reader user's page is the loss this accepts.
function isDarkReaderHydrationNoise(
  properties: Record<string, unknown> | undefined,
  doc: TranslationProbeDocument | undefined,
): boolean {
  return (
    doc != null &&
    exceptionEntries(properties).some(
      (entry) => typeof entry.value === "string" && ELEMENT_HYDRATION_MISMATCH_RE.test(entry.value),
    ) &&
    isDarkReaderPresent(doc)
  )
}

type ExceptionEntry = {
  type?: unknown
  value?: unknown
  mechanism?: { handled?: unknown } | null
  stacktrace?: { frames?: Array<{ filename?: unknown; abs_path?: unknown }> } | null
}

function exceptionEntries(properties: Record<string, unknown> | undefined): ExceptionEntry[] {
  const list = properties?.$exception_list
  return Array.isArray(list) ? (list.filter((entry) => entry && typeof entry === "object") as ExceptionEntry[]) : []
}

/** True when an exception message alone is known browser noise. The server
 * side that files GitHub issues from a PostHog issue (the alert webhook and the
 * polling backstop) has only the issue's message, so it asks this, or a closed
 * noise issue comes back as a new one while browsers keep sending the event. */
export function isBrowserNoiseMessage(message: string): boolean {
  return message !== "" && (MASKED_SCRIPT_ERROR_RE.test(message) || KNOWN_NOISE_MESSAGE_RE.some((re) => re.test(message)))
}

/** True when a `$exception` event's properties describe browser noise. */
export function isBrowserNoiseException(
  properties: Record<string, unknown> | undefined,
  options: { keepMaskedScriptError?: boolean } = {},
): boolean {
  for (const entry of exceptionEntries(properties)) {
    const frames = entry.stacktrace?.frames ?? []
    const files = frames.map((frame) => `${frame?.filename ?? ""} ${frame?.abs_path ?? ""}`).join("\n")
    if (EXTENSION_SCHEME_RE.test(files)) return true
    if (entry.type === "AbortError") return true
    const value = typeof entry.value === "string" ? entry.value : ""
    if (options.keepMaskedScriptError && MASKED_SCRIPT_ERROR_RE.test(value)) continue
    if (isBrowserNoiseMessage(value)) return true
  }
  return false
}

/** The exception properties with every message's secret-shaped values
 * redacted. Returns the same object when nothing changed. */
export function redactExceptionProperties<T extends Record<string, unknown>>(properties: T): T {
  return sanitizeErrorDiagnosticValue(properties) as T
}

/** The marker the money-page listener puts on the exceptions it captures. */
export const MONEY_PATH_LISTENER_CAPTURE = "money-path-listener"

/** `ca_capture` on the exception the route and root error boundaries send. */
export const ERROR_BOUNDARY_CAPTURE = "error-boundary"

type TranslationProbeDocument = {
  documentElement: { className: string }
  querySelector: (selector: string) => unknown
  /** The window, where the root layout's guard script latches translation. */
  defaultView?: object | null
}

/** Whether the browser translated the page: Chrome sets a translated-ltr or
 * translated-rtl class on <html>, Google Translate wraps text in
 * <font style="vertical-align: inherit">, and the guard script in
 * web/lib/translate-dom-guard.ts latches the class the moment it appears,
 * because React's client re-render after a hydration mismatch resets <html>. */
function isPageTranslated(doc: TranslationProbeDocument): boolean {
  return (
    Reflect.get(doc.defaultView ?? {}, PAGE_TRANSLATED_FLAG) === true ||
    /\btranslated-(?:ltr|rtl)\b/.test(doc.documentElement.className) ||
    doc.querySelector("font[style*='vertical-align']") != null
  )
}

/** Whether the Dark Reader extension is acting on the page: the guard script
 * latches its `data-darkreader-*` attributes on <html> the moment they appear,
 * because React's client re-render after the mismatch removes them, and the
 * extension's <style class="darkreader"> and <meta name="darkreader"> nodes in
 * <head> are the fallback when the latch is missing. */
function isDarkReaderPresent(doc: TranslationProbeDocument): boolean {
  return (
    Reflect.get(doc.defaultView ?? {}, DARK_READER_FLAG) === true ||
    doc.querySelector("html[data-darkreader-mode], style.darkreader, meta[name='darkreader']") != null
  )
}

/** The properties an error boundary adds to its `captureException`: the
 * capture tag, the digest, and whether the browser had translated the page.
 * A translated page is the known cause of the error page for non-English
 * visitors, so this lets PostHog count those views apart from real defects. */
export function errorBoundaryCaptureProperties(
  digest: string | undefined,
  doc: TranslationProbeDocument,
): Record<string, unknown> {
  return {
    ca_capture: ERROR_BOUNDARY_CAPTURE,
    ca_page_translated: isPageTranslated(doc),
    ...(digest ? { digest } : {}),
  }
}

type ExceptionListenerTarget = Pick<EventTarget, "addEventListener"> & { location: { pathname: string } }

/**
 * posthog-js's own unhandled-error autocapture passes every error through a
 * per-browser limiter: ten per exception type in a burst, then one every ten
 * seconds. On a pay or apply page nothing may be dropped, so these listeners
 * capture every unhandled error and rejection there through
 * `posthog.captureException` (`capture`), which that limiter never sees. The
 * page is read when the error happens, so a client-side navigation counts.
 * scrubBrowserExceptionEvent then drops the autocaptured twin of the same
 * error, so each is sent once. Every other page keeps the SDK's own limiter.
 * Returns true once installed; the `before_send` passes that on as
 * `moneyPathListener`.
 */
export function captureMoneyPathUnhandledExceptions(
  target: ExceptionListenerTarget,
  capture: (error: unknown, properties: Record<string, unknown>) => void,
): boolean {
  const properties = { ca_capture: MONEY_PATH_LISTENER_CAPTURE }
  // posthog-js reads an ErrorEvent's error and a rejection event's reason itself.
  const onError = (event: Event) => {
    if (isMoneyPath(target.location.pathname)) capture(event, properties)
  }
  target.addEventListener("error", onError)
  target.addEventListener("unhandledrejection", onError)
  return true
}

/** posthog-js's own autocapture marks the first entry `handled: false`;
 * `captureException` marks it `handled: true`. */
function isAutocapturedUnhandled(properties: Record<string, unknown>): boolean {
  return exceptionEntries(properties)[0]?.mechanism?.handled === false
}

/** The listener captures through `captureException`, which marks the error
 * handled; it was not, so the entries say so. */
function markUnhandled<T extends Record<string, unknown>>(properties: T): T {
  const list = exceptionEntries(properties).map((entry) => ({
    ...entry,
    mechanism: { ...(entry.mechanism ?? {}), handled: false },
  }))
  return { ...properties, $exception_list: list }
}

/** The browser `before_send` for a `$exception` event. Outside production
 * every exception is dropped: posthog-js runs on localhost with the live
 * project token, and `posthog.captureException` ignores `capture_exceptions`,
 * so this is what keeps a local error out of PostHog and GitHub. In production
 * noise is dropped, secret-shaped values in the message are redacted, and an
 * exception raised on a money path is marked for the rate-limit bypass. With
 * `moneyPathListener`, the autocaptured copy of an unhandled error on a money
 * path is dropped because the listener sent it already. With `document`, a
 * hydration mismatch on a page the browser translated, and an element mismatch
 * on a page Dark Reader acted on, is dropped off the money paths. Any other event passes through unchanged. `pathname` is the page the
 * visitor is on. */
export function scrubBrowserExceptionEvent(
  event: CaptureResult | null,
  pathname: string,
  options: { moneyPathListener?: boolean; document?: TranslationProbeDocument } = {},
): CaptureResult | null {
  if (!event || event.event !== "$exception") return event
  if (process.env.NODE_ENV !== "production") return null
  const properties = (event.properties ?? {}) as Record<string, unknown>
  // An error-page view is counted even when its message is known browser noise
  // (a translated page's insertBefore error is the live case): the boundary
  // capture is the only record that a visitor saw the error page. It is marked
  // (ca_browser_noise) rather than dropped, and only when the MESSAGE is the
  // noise, because the server side that files GitHub issues (the alert webhook
  // and the polling backstop) skips known noise by message alone. Noise
  // recognised any other way (an extension frame, an AbortError, Opera's, a
  // translated page's or a Dark Reader page's hydration mismatch) is still dropped, or it would file an
  // issue.
  const fromBoundary = properties.ca_capture === ERROR_BOUNDARY_CAPTURE
  const onMoneyPath = isMoneyPath(pathname)
  const noise = isBrowserNoiseException(properties, { keepMaskedScriptError: onMoneyPath })
  const hydrationNoise =
    !onMoneyPath &&
    (isOperaHydrationNoise(properties) ||
      isTranslatedHydrationNoise(properties, options.document) ||
      isDarkReaderHydrationNoise(properties, options.document))
  const messageNoise = exceptionEntries(properties).some(
    (entry) => typeof entry.value === "string" && isBrowserNoiseMessage(entry.value),
  )
  if ((noise || hydrationNoise) && !(fromBoundary && messageNoise)) return null
  const fromListener = properties.ca_capture === MONEY_PATH_LISTENER_CAPTURE
  if (onMoneyPath && options.moneyPathListener && !fromListener && isAutocapturedUnhandled(properties)) return null
  const redacted = redactExceptionProperties(properties)
  const flagged = fromBoundary && messageNoise ? { ...redacted, ca_browser_noise: true } : redacted
  const unhandled = fromListener ? markUnhandled(flagged) : flagged
  const marked = onMoneyPath ? { ...unhandled, ca_money_path: true } : unhandled
  return marked === properties ? event : ({ ...event, properties: marked } as CaptureResult)
}
